/*
  NBV2 Platform Hero (sections/nbv2-platform-hero.liquid)

  Turns the card row into a wheel, like the card carousel on toss.im: every
  card hangs on the rim of a large invisible circle whose centre sits far
  below the section, and turning the circle carries cards along the arc. They
  tilt and dip on the way out and straighten on the way to the middle.

  - `position` is a float in card units that is never wrapped. Each card's
    offset from it is folded into [-n/2, n/2), so the wheel loops forever:
    a card leaving one edge comes back in at the other while it is hidden.
  - Five cards do not fill a wide screen, so the set is cloned until the
    loop point sits past both edges. Clones are aria-hidden and inert.
  - The wheel drifts continuously: one rAF loop moves `position` by
    `velocity`, which ramps smoothly toward the drift speed (one card per
    "Time per card") or toward zero while drifting is not allowed.
  - Arrows and the theme editor set a `target`; the loop eases onto it and
    then lets the drift ramp back up from rest.
  - Dragging moves the wheel directly; letting go hands the flick velocity
    to the loop, which decays it back into the drift.
  - The loop stops when nothing moves (off screen, hidden tab, reduced
    motion) and restarts when that changes.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nbv2-platform-hero]';
  var EASE_MS = 150; /* time constant: a one-card nudge settles in ~750ms */
  var RAMP_MS = 400; /* time constant for the drift speeding up / slowing down */
  var MAX_FLING = 0.01; /* cards per ms */
  var MAX_CLONE_SETS = 6;

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function init(root) {
    if (root.nbv2PlatformHeroInit === true) {
      return;
    }

    var wheel = root.querySelector('[data-nbv2-ph-wheel]');
    var track = root.querySelector('[data-nbv2-ph-track]');
    var prev = root.querySelector('[data-nbv2-ph-prev]');
    var next = root.querySelector('[data-nbv2-ph-next]');

    if (!wheel || !track) {
      return;
    }

    var originals = Array.prototype.slice.call(track.querySelectorAll('[data-nbv2-ph-card]'));
    var count = originals.length;

    if (count < 2) {
      return;
    }

    root.nbv2PlatformHeroInit = true;

    var arcStep = (parseFloat(root.getAttribute('data-arc-step')) || 0) * (Math.PI / 180);
    var autoplayEnabled = root.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 3000;

    var cards = originals.slice();
    var cardWidth = 0;
    var pitch = 0; /* card width + gap: distance between neighbouring centres */
    var radius = 0;
    var driftSpeed = 1 / interval; /* cards per ms */
    var position = 0;
    var velocity = 0; /* cards per ms */
    var target = null; /* set while an arrow / editor nudge is easing in */
    var frame = null;
    var lastTime = 0;
    var focused = false;
    var inView = true;
    var editorHold = false;
    var drag = null;

    /* Geometry ------------------------------------------------------------ */

    function measure() {
      cardWidth = originals[0].offsetWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap);
      if (isNaN(gap)) {
        gap = 0;
      }
      pitch = cardWidth + gap;

      /*
        Neighbouring card centres sit one pitch apart along the chord, so a
        tilt of arcStep between them needs this radius. With no tilt the rim is
        flat and cards simply slide sideways.
      */
      radius = arcStep > 0 ? pitch / (2 * Math.sin(arcStep / 2)) : 0;
    }

    /* How far either side of centre a card has to travel to be off screen. */
    function halfSpanInCards() {
      var half = wheel.clientWidth / 2 + cardWidth;
      if (radius > 0) {
        var ratio = Math.min(half / radius, 1);
        return Math.asin(ratio) / arcStep;
      }
      return half / pitch;
    }

    /*
      A card jumps ends when its offset crosses ±n/2, so n/2 has to be at least
      one card past the visible half-span or the jump would be seen.
    */
    function ensureClones() {
      var needed = Math.ceil(halfSpanInCards() + 1.5) * 2;
      var sets = 0;

      while (cards.length < needed && sets < MAX_CLONE_SETS) {
        originals.forEach(function (card) {
          var clone = card.cloneNode(true);
          clone.removeAttribute('data-shopify-editor-block');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.classList.add('is-clone');
          track.appendChild(clone);
          cards.push(clone);
        });
        sets += 1;
      }
    }

    function layout() {
      var n = cards.length;
      var half = n / 2;
      var limit = halfSpanInCards();

      for (var i = 0; i < n; i += 1) {
        var offset = i - position;
        offset = ((((offset + half) % n) + n) % n) - half;

        var card = cards[i];
        if (Math.abs(offset) > limit + 0.5) {
          card.style.visibility = 'hidden';
          continue;
        }
        card.style.visibility = '';

        if (radius > 0) {
          card.style.transform = 'rotate(' + (offset * arcStep) + 'rad)';
        } else {
          card.style.transform = 'translate3d(' + (offset * pitch) + 'px, 0, 0)';
        }
      }
    }

    function applyOrigin() {
      var origin = radius > 0 ? '50% calc(50% + ' + radius + 'px)' : '';
      cards.forEach(function (card) {
        card.style.transformOrigin = origin;
      });
    }

    function refresh() {
      measure();
      ensureClones();
      applyOrigin();
      layout();
    }

    /* Motion ------------------------------------------------------------- */

    function driftAllowed() {
      return (
        autoplayEnabled &&
        !prefersReducedMotion() &&
        !focused &&
        !editorHold &&
        inView &&
        !document.hidden
      );
    }

    function isMoving() {
      return target !== null || Math.abs(velocity) > 1e-6 || driftAllowed();
    }

    function tick(time) {
      frame = null;

      /* While a drag is live the pointer drives the wheel; the loop idles. */
      if (drag && drag.active) {
        lastTime = 0;
        return;
      }

      var dt = lastTime ? Math.min(time - lastTime, 64) : 16;
      lastTime = time;

      if (target !== null) {
        var diff = target - position;
        if (Math.abs(diff) < 0.0005) {
          position = target;
          target = null;
          velocity = 0;
        } else {
          position += diff * (1 - Math.exp(-dt / EASE_MS));
        }
      } else {
        var desired = driftAllowed() ? driftSpeed : 0;
        velocity += (desired - velocity) * (1 - Math.exp(-dt / RAMP_MS));
        if (desired === 0 && Math.abs(velocity) <= 1e-6) {
          velocity = 0;
        }
        position += velocity * dt;
      }

      layout();

      if (isMoving()) {
        frame = window.requestAnimationFrame(tick);
      } else {
        lastTime = 0;
      }
    }

    function run() {
      if (frame === null && isMoving()) {
        lastTime = 0;
        frame = window.requestAnimationFrame(tick);
      }
    }

    function nudgeTo(value) {
      target = value;
      velocity = 0;
      if (prefersReducedMotion()) {
        position = target;
        target = null;
        layout();
        return;
      }
      run();
    }

    /*
      Exactly one card from wherever the wheel is: while it drifts it never
      rests on a card, so snapping would make the jump uneven.
    */
    function nudge(direction) {
      var base = target !== null ? target : position;
      nudgeTo(base + direction);
    }

    /* Input -------------------------------------------------------------- */

    function onPrev() {
      nudge(-1);
    }

    function onNext() {
      nudge(1);
    }

    function onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }
      if (event.target.closest && event.target.closest('.nbv2-platform-hero__nav')) {
        return;
      }
      drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startPos: 0,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
        active: false
      };
    }

    function onPointerMove(event) {
      if (!drag || event.pointerId !== drag.id) {
        return;
      }

      if (!drag.active) {
        var dx0 = event.clientX - drag.startX;
        var dy = event.clientY - drag.startY;
        if (Math.abs(dx0) < 6 || Math.abs(dx0) < Math.abs(dy)) {
          return;
        }
        /* The wheel kept drifting until now, so grab it where it is. */
        drag.active = true;
        drag.startX = event.clientX;
        drag.startPos = position;
        target = null;
        velocity = 0;
        wheel.classList.add('is-dragging');
        if (wheel.setPointerCapture) {
          wheel.setPointerCapture(event.pointerId);
        }
      }

      var dt = event.timeStamp - drag.lastTime;
      if (dt > 0) {
        drag.velocity = (event.clientX - drag.lastX) / dt;
      }
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;

      position = drag.startPos - (event.clientX - drag.startX) / pitch;
      layout();
    }

    function onPointerUp(event) {
      if (!drag || event.pointerId !== drag.id) {
        return;
      }

      var wasActive = drag.active;
      var fling = -drag.velocity / pitch; /* cards per ms */
      drag = null;
      wheel.classList.remove('is-dragging');

      if (!wasActive) {
        return;
      }

      fling = Math.max(-MAX_FLING, Math.min(MAX_FLING, fling));

      if (driftAllowed()) {
        /* Hand the flick to the loop; it decays back into the drift. */
        velocity = fling;
        run();
      } else {
        /* No drift to settle into, so land on the nearest card. */
        nudgeTo(Math.round(position + fling * 200));
      }
    }

    /* Keyboard focus only: a mouse click on an arrow also focuses it. */
    function onFocusIn(event) {
      var keyboard = true;
      try {
        keyboard = event.target.matches(':focus-visible');
      } catch (error) {
        /* Older browsers without :focus-visible: treat all focus as keyboard. */
      }
      if (!keyboard) {
        return;
      }
      focused = true;
      run();
    }

    function onFocusOut(event) {
      if (!root.contains(event.relatedTarget)) {
        focused = false;
        run();
      }
    }

    function onVisibilityChange() {
      run();
    }

    var resizeFrame = null;
    function onResize() {
      if (resizeFrame !== null) {
        return;
      }
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = null;
        refresh();
      });
    }

    /* Theme editor: turn the selected block to the middle and hold it there. */
    function onBlockSelect(event) {
      var index = originals.indexOf(event.target);
      if (index === -1) {
        return;
      }
      editorHold = true;
      var n = cards.length;
      var from = Math.round(position);
      var offset = ((((index - from + n / 2) % n) + n) % n) - n / 2;
      nudgeTo(from + offset);
    }

    function onBlockDeselect(event) {
      if (originals.indexOf(event.target) === -1) {
        return;
      }
      editorHold = false;
      run();
    }

    /* Setup -------------------------------------------------------------- */

    track.classList.add('is-ready');
    wheel.classList.add('is-draggable');
    refresh();

    if (prev) {
      prev.addEventListener('click', onPrev);
    }
    if (next) {
      next.addEventListener('click', onNext);
    }

    wheel.addEventListener('pointerdown', onPointerDown);
    wheel.addEventListener('pointermove', onPointerMove);
    wheel.addEventListener('pointerup', onPointerUp);
    wheel.addEventListener('pointercancel', onPointerUp);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);
    window.addEventListener('resize', onResize);

    var observer = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        run();
      });
      observer.observe(root);
    }

    run();

    root.nbv2PlatformHeroDestroy = function () {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
      if (prev) {
        prev.removeEventListener('click', onPrev);
      }
      if (next) {
        next.removeEventListener('click', onNext);
      }
      wheel.removeEventListener('pointerdown', onPointerDown);
      wheel.removeEventListener('pointermove', onPointerMove);
      wheel.removeEventListener('pointerup', onPointerUp);
      wheel.removeEventListener('pointercancel', onPointerUp);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
      root.nbv2PlatformHeroInit = false;
      root.nbv2PlatformHeroDestroy = null;
    };
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    if (container.nodeType === 1 && container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (typeof root.nbv2PlatformHeroDestroy === 'function') {
        root.nbv2PlatformHeroDestroy();
      }
    });
  });
})();
